import { useState } from 'react';

import { api } from '../../api';
import { ownersErrorMessage } from '../../lib/boatOwners';

// Inline two-step arm → fire block for removing a boat from the signed-in
// user's account (ADR 0165). Same shape as the alarm delete confirm on
// AlarmDetail — the trigger is a `.btn-danger`, the armed state replaces it
// with a card that names the consequence, and both verbs disable while the
// request is in flight.
//
// The consequence is the whole point of the copy: the boat's data is RETAINED
// (an admin can restore it), but the user loses access to it. Promising
// "deleted forever" would be a lie, and promising "you can undo this" would
// over-promise a support action. So the warning says exactly what the user
// loses, and nothing about what happens on our side.
//
// DELETE /v1/boats/:boatId requires an Idempotency-Key.

interface DeleteBoatConfirmProps {
  boatId: string;
  boatName: string;
  /** Called after the server has accepted the delete. */
  onDeleted: () => void;
}

export function DeleteBoatConfirm({
  boatId,
  boatName,
  onDeleted,
}: DeleteBoatConfirmProps): React.ReactElement {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doDelete(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/boats/${boatId}`, {
        method: 'DELETE',
        idempotencyKey: crypto.randomUUID(),
      });
      onDeleted();
    } catch (err) {
      // A boat that gained another owner since the page loaded is refused with
      // 409 boat_has_other_owners (TOM-458): that one has a sentence of its own.
      const apiMessage = (err as { body?: { message?: string } }).body?.message;
      setError(ownersErrorMessage(err, apiMessage ?? 'Could not remove this boat. Try again.'));
      setBusy(false);
    }
  }

  if (!armed) {
    return (
      <button
        type="button"
        className="btn btn-danger"
        data-testid="boat-delete-btn"
        onClick={() => {
          setError(null);
          setArmed(true);
        }}
      >
        Delete this boat
      </button>
    );
  }

  return (
    <div className="alarm-delete-confirm" data-testid="boat-delete-confirm">
      <p>
        Delete <strong>{boatName}</strong> from your account? You will no longer have access to this
        boat or its data.
      </p>
      <div className="alarm-form-actions">
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy}
          data-testid="boat-delete-confirm-btn"
          onClick={() => void doDelete()}
        >
          {busy ? 'Removing…' : 'Delete boat'}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => setArmed(false)}>
          Keep it
        </button>
      </div>
      {error !== null ? (
        <p className="alarm-form-err" role="alert" data-testid="boat-delete-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
